/**
 * Persistent fallback and auto-rotation controller for cart-drawer recommendations.
 *
 * Shopify can hydrate the drawer with replacement HTML without executing its
 * inline scripts. This module manages Add-to-cart buttons, collapsible FBT toggles,
 * and smooth auto-rotation with interactive pause/resume for recommendation carousels.
 */

(function () {
  'use strict';

  const ROTATION_INTERVAL = 3200;
  const activeRotations = new Map();

  function getCardStep(track) {
    const card = track.querySelector('.fbt-card');
    if (card instanceof HTMLElement) {
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.gap) || 8;
      return card.offsetWidth + gap;
    }
    return track.clientWidth * 0.5;
  }

  function updateCarouselArrows(track, prevBtn, nextBtn) {
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 4) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
    } else {
      if (prevBtn) {
        prevBtn.style.display = 'flex';
        prevBtn.disabled = track.scrollLeft <= 4;
      }
      if (nextBtn) {
        nextBtn.style.display = 'flex';
        nextBtn.disabled = track.scrollLeft >= maxScroll - 4;
      }
    }
  }

  function setupCarouselAutoRotate(track) {
    if (!(track instanceof HTMLElement) || !track.isConnected) return;
    if (activeRotations.has(track)) return;

    const wrapper = track.closest('.fbt-carousel-wrapper') || track.parentElement;
    const drawer = track.closest('theme-drawer') || document.querySelector('theme-drawer#cart-drawer');
    const container = track.closest('.cart-drawer__empty-upsells, .fbt-container, #cart-drawer-fbt') || wrapper;
    const prevBtn = container?.querySelector('.fbt-carousel__btn--prev') || wrapper?.querySelector('.fbt-carousel__btn--prev');
    const nextBtn = container?.querySelector('.fbt-carousel__btn--next') || wrapper?.querySelector('.fbt-carousel__btn--next');

    let timer = null;
    let isPaused = false;

    const stepNext = () => {
      if (document.hidden) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (drawer && !drawer.hasAttribute('open')) return;
      if (isPaused) return;
      if (!track.offsetParent) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 6) return;

      const step = getCardStep(track);
      if (track.scrollLeft >= maxScroll - 8) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: step, behavior: 'smooth' });
      }
      setTimeout(() => updateCarouselArrows(track, prevBtn, nextBtn), 320);
    };

    const start = () => {
      stop();
      if (drawer && !drawer.hasAttribute('open')) return;
      timer = window.setInterval(stepNext, ROTATION_INTERVAL);
    };

    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const pause = () => {
      isPaused = true;
      stop();
    };

    const resume = () => {
      isPaused = false;
      start();
    };

    if (wrapper) {
      wrapper.addEventListener('mouseenter', pause);
      wrapper.addEventListener('mouseleave', resume);
      wrapper.addEventListener('focusin', pause);
      wrapper.addEventListener('focusout', resume);
      wrapper.addEventListener('touchstart', pause, { passive: true });
      wrapper.addEventListener('touchend', resume, { passive: true });
      wrapper.addEventListener('touchcancel', resume, { passive: true });
    }

    if (prevBtn && prevBtn.dataset.fbtBtnBound !== 'true') {
      prevBtn.dataset.fbtBtnBound = 'true';
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const step = getCardStep(track);
        track.scrollBy({ left: -step, behavior: 'smooth' });
        pause();
        setTimeout(resume, 4000);
        setTimeout(() => updateCarouselArrows(track, prevBtn, nextBtn), 320);
      });
    }

    if (nextBtn && nextBtn.dataset.fbtBtnBound !== 'true') {
      nextBtn.dataset.fbtBtnBound = 'true';
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const step = getCardStep(track);
        track.scrollBy({ left: step, behavior: 'smooth' });
        pause();
        setTimeout(resume, 4000);
        setTimeout(() => updateCarouselArrows(track, prevBtn, nextBtn), 320);
      });
    }

    track.addEventListener('scroll', () => updateCarouselArrows(track, prevBtn, nextBtn), { passive: true });
    updateCarouselArrows(track, prevBtn, nextBtn);
    setTimeout(() => updateCarouselArrows(track, prevBtn, nextBtn), 250);

    start();

    activeRotations.set(track, {
      start,
      stop,
      pause,
      resume,
      update: () => updateCarouselArrows(track, prevBtn, nextBtn),
    });
  }

  function initAllCartCarousels() {
    const tracks = document.querySelectorAll(
      '.cart-drawer__empty-upsells .fbt-carousel__track, #cart-drawer-fbt .fbt-carousel__track, .fbt-carousel__track'
    );
    tracks.forEach((track) => {
      const active = activeRotations.get(track);
      if (active) {
        active.start();
        active.update();
      } else {
        setupCarouselAutoRotate(track);
      }
    });
  }

  function stopAllCartCarousels() {
    activeRotations.forEach((rotation, track) => {
      if (!track.isConnected) {
        rotation.stop();
        activeRotations.delete(track);
        return;
      }
      rotation.stop();
    });
  }

  // Keep recommendation work off the page startup path. Initialize only while
  // the shopper has the cart drawer open, and stop timers as soon as it closes.
  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    requestAnimationFrame(initAllCartCarousels);
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id === 'cart-drawer') stopAllCartCarousels();
  });

  document.addEventListener('cart:updated', () => {
    if (document.querySelector('theme-drawer#cart-drawer[open]')) {
      setTimeout(initAllCartCarousels, 400);
    }
  });

  // Watch for cart drawer open attribute changes
  const drawerEl = document.querySelector('theme-drawer#cart-drawer');
  if (drawerEl) {
    const drawerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          if (drawerEl.hasAttribute('open')) {
            requestAnimationFrame(initAllCartCarousels);
          } else {
            stopAllCartCarousels();
          }
        }
      }
    });
    drawerObserver.observe(drawerEl, { attributes: true, attributeFilter: ['open'] });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopAllCartCarousels();
    } else if (drawerEl?.hasAttribute('open')) {
      requestAnimationFrame(initAllCartCarousels);
    }
  });

  // Persistent delegated click listener for Add buttons & FBT accordion toggle
  if (!window.__bubbleCartFbtFallbackBound) {
    window.__bubbleCartFbtFallbackBound = true;

    document.addEventListener('click', async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const toggle = target.closest('#cart-drawer-fbt-toggle');
      if (toggle instanceof HTMLButtonElement && toggle.dataset.fbtToggleBound !== 'true') {
        const container = toggle.closest('#cart-drawer-fbt');
        const content = container?.querySelector('#cart-drawer-fbt-content');
        if (container && content) {
          event.preventDefault();
          event.stopPropagation();
          const isCollapsed = container.classList.toggle('is-collapsed');
          toggle.setAttribute('aria-expanded', String(!isCollapsed));
        }
        return;
      }

      const button = target.closest('.fbt-card__add-btn');
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.dataset.fbtAddBound === 'true') return;

      event.preventDefault();
      event.stopPropagation();

      if (button.disabled || button.classList.contains('is-loading')) return;

      const variantId = Number(button.dataset.variantId);
      if (!Number.isSafeInteger(variantId) || variantId <= 0) return;

      const label = button.querySelector('.fbt-card__add-label');
      const originalLabel = label?.textContent || 'ADD';
      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const response = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: ['cart-drawer-section'],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }

        button.classList.remove('is-loading');
        button.classList.add('is-success');
        if (label) label.textContent = 'Added ✓';

        const sectionHtml = data.sections?.['cart-drawer-section'];
        if (sectionHtml) {
          const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const fresh = parsed.querySelector('[data-hydration-key="cart-drawer-inner"]');
          const current = document.querySelector('[data-hydration-key="cart-drawer-inner"]');

          if (fresh && current) {
            window.setTimeout(() => {
              current.replaceWith(fresh);
              initAllCartCarousels();
            }, 350);
          }
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: data },
          })
        );
      } catch (error) {
        console.error('[cart-fbt] Add failed:', error);
        button.classList.remove('is-loading');
        button.disabled = false;
        if (label) label.textContent = 'Retry';

        window.setTimeout(() => {
          if (label?.isConnected) label.textContent = originalLabel;
        }, 1800);
      }
    });
  }
})();
